<template>
  <div
    v-if="isVisibleComponent"
    data-qa="award-winner"
  >
    <div
      class="d-inline-flex align-center py-1 px-2 rounded bg-warning cursor-pointer"
      @click.prevent="isActive = !isActive"
    >
      <div class="ui-icon-rating-40 ui-icon-fz-medium rating--text" />
      <div class="text-body-2 text--text ml-2">
        {{ title }}
      </div>
    </div>
    <AwardDialog
      :type="type"
      :title="title"
      :is-active="isActive"
      :current-award-year="currentAwardYear"
      @input="handleInput"
      @redirect="handleRedirect"
    />
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  Ref,
  nextTick,
  computed,
} from 'vue'
import i18next from 'i18next'
import AwardDialog from 'src/components/common/AwardWinner/components/AwardDialog.vue'
import smoothScrollTo from 'src/utils/smoothScrollTo'

const SCROLL_MS = 600

const props = defineProps<{
  type: 'doctor' | 'lpu'
  redirectUrl: string
  currentAwardYear: number
  isAwardOrGrandPrixWinner: boolean
}>()
const isActive: Ref<boolean> = ref(false)
const isVisibleComponent: Ref<boolean> = computed(() => Boolean(
  props.currentAwardYear && props.isAwardOrGrandPrixWinner,
))
const title = `${i18next.t('Выбор пациентов', { ns: i18next.language })} ${props.currentAwardYear}`

function handleInput(val: boolean) {
  isActive.value = val
}

/** Offset под sticky SectionMenu и preview-bar — как у якорей LPU-страницы. */
function getSamePageHashScrollOffset() {
  const menu = document.querySelector<HTMLElement>('[data-qa="section_menu"]')
  const menuHeight = menu?.offsetHeight ?? 0
  const cssOffset = getComputedStyle(document.documentElement)
    .getPropertyValue('--preview-menu-offset')
    .trim()
  const previewHeight = cssOffset.endsWith('px')
    ? Number.parseFloat(cssOffset) || 0
    : 0

  return menuHeight + previewHeight
}

/**
 * Same-document `#hash`: без `location.assign`, иначе браузер мгновенно прыгает
 * и sticky-меню не успевает закрепиться. `pushState` + плавный скролл.
 */
function scrollToSamePageHash(url: URL) {
  const target = document.querySelector<HTMLElement>(url.hash)

  if (!target) {
    return false
  }

  window.history.pushState(null, '', `${url.pathname}${url.search}${url.hash}`)

  const targetTop = target.getBoundingClientRect().top + window.scrollY

  smoothScrollTo(
    Math.max(targetTop - getSamePageHashScrollOffset(), 0),
    { duration: SCROLL_MS },
  )

  return true
}

async function handleRedirect() {
  isActive.value = false
  await nextTick()

  try {
    const url = new URL(props.redirectUrl, window.location.href)
    const isSameDocument = url.pathname === window.location.pathname
      && url.search === window.location.search

    if (isSameDocument && url.hash && scrollToSamePageHash(url)) {
      return
    }
  } catch {
    // невалидный redirectUrl — ниже assign
  }

  window.location.assign(props.redirectUrl)
}
</script>
